<template>
  <b-row>
    <b-col cols="12" md="10" class="mx-auto">
      <div role="list" class="faq-list">
        <FaqItem
          v-for="(faq, index) in faqData"
          :key="index"
          :faq="faq"
          :index="index"
          :isOpen="openQuestion === index"
          @toggle="toggleQuestion"
        />
      </div>
    </b-col>
  </b-row>
</template>

<script setup>
import { ref, computed, watchEffect } from 'vue'
import FaqItem from './FAQitem_prp_home.vue'
import { useStore } from 'vuex'

const store = useStore()
const schemeData = computed(() => store.getters.schemeData)

const openQuestion = ref(null)

const faqData = ref([
  {
    question: 'What is the current NAV of?',
    answer: 'Loading NAV...',
  },
  {
    question: 'What are the historical returns of?',
    answer:
      'We give recommendations on stocks, mutual funds, ETFs, and bonds. We also review a wide range of other useful products, including deposits, health insurance, term insurance & more.',
  },
  {
    question: 'What are the major holdings of?',
    answer:
      'You can access our full platform through a simple subscription plan. Check out our pricing page to know more about our plans, what you will get in your subscription, and the subscription costs.',
  },
  {
    question: 'What is the asset allocation of?',
    answer:
      'Yes, we offer a free 7-day trial that gives you access to the platform. All you need to do is to register and set up an account with PrimeInvestor.',
  },
  {
    question: 'Who is the fund manager of?',
    answer:
      'Subscription fees are annual/quarterly. We do not offer refunds during a period of subscription. However, renewals can be cancelled at any time.',
  },
  {
    question: 'What is the minimum investment amount?',
    answer:
      'Subscription fees are annual/quarterly. We do not offer refunds during a period of subscription. However, renewals can be cancelled at any time.',
  },
  {
    question: 'What is the fund’s expense ratio?',
    answer:
      'Subscription fees are annual/quarterly. We do not offer refunds during a period of subscription. However, renewals can be cancelled at any time.',
  },
  {
    question: 'How are the gains from taxed?',
    answer:
      'Subscription fees are annual/quarterly. We do not offer refunds during a period of subscription. However, renewals can be cancelled at any time.',
  },
  {
    question: 'What type of investor is suitable for?',
    answer:
      'Subscription fees are annual/quarterly. We do not offer refunds during a period of subscription. However, renewals can be cancelled at any time.',
  },
])
const scheme = computed(() => schemeData.value?.schemedetails || {})
const categoryName = computed(() => scheme.value?.subcategory || '')

const cleanFundName = (fundName) => {
  const index = fundName.indexOf('(G)')
  return index !== -1 ? fundName.slice(0, index).trim() : fundName.trim()
}
const formatDate = (dateStr) => {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}
const emit = defineEmits(['set-fund-info'])

watchEffect(() => {
  const scheme = schemeData.value?.schemedetails
  const categoryName = scheme?.subcategory
  const nav = scheme?.nav ? Number(scheme.nav).toFixed(2) : '0.00'
  const navDate = scheme?.nav_date
  const suitabilityText = scheme?.suitability_text
  const performance = schemeData.value?.performance
  const schemeassetallocation = schemeData.value?.schemeassetallocation
  const schemeManager = scheme?.fund_managers
  const minimumInvestment = scheme?.min_investment
  const assetAllocationEQ =
    schemeassetallocation?.EquityAllocationPercentage!= null
      ? parseFloat(schemeassetallocation.EquityAllocationPercentage).toFixed(2) + '%'
      : '0.00%'

  const assetAllocationDT =
    schemeassetallocation?.DebtAllocationPercentage != null
      ? parseFloat(schemeassetallocation.DebtAllocationPercentage).toFixed(2) + '%'
      : '0.00%'

  const assetAllocationCash =
    schemeassetallocation?.OtherAllocationPercentage != null
      ? parseFloat(schemeassetallocation.OtherAllocationPercentage).toFixed(2) + '%'
      : '0.00%'

  const schemeTopsectiorsEQ = schemeData.value?.lastSixmonthmove.EQ
  const schemeTopsectiorsDT = schemeData.value?.lastSixmonthmove.DT

  const lastSixmonthmove = computed(() => {
    const eq = schemeTopsectiorsEQ || []
    const dt = schemeTopsectiorsDT || []

    return {
      EQ: eq.map((company) => ({
        companyName: company.companyName,
        sector: company.sector,
        FINCODE: company.FINCODE,
        pe: company.PE,
        netAssetsPercentage: company.HOLDPERCENTAGE,
        monthly_records: company.monthly_records || [],
      })),
      DT: dt.map((company) => ({
        companyName: company.companyName,
        sector: company.sector,
        FINCODE: company.FINCODE,
        netAssetsPercentage: company.HOLDPERCENTAGE,
        monthly_records: company.monthly_records || [],
      })),
    }
  })

  const topCompaniesEQ = lastSixmonthmove.value.EQ.map(
    (company) => company.companyName
  )

  const topCompaniesDT = lastSixmonthmove.value.DT.map(
    (company) => company.companyName
  )
  const baseUrl = process.env.VUE_APP_BASE_URL
  const expenseRatio = schemeData.value?.schemedetails.expense_ratio
    ? `${parseFloat(schemeData.value?.schemedetails.expense_ratio).toFixed(2)}%`
    : '—'
  if (
    scheme?.scheme_name &&
    scheme?.plan !== undefined &&
    nav &&
    navDate &&
    performance
  ) {
    const fundName = cleanFundName(scheme.scheme_name)
    const planLabel =
      scheme.plan === '5' ? 'Direct - Growth' : 'Regular - Growth'
    const fullFundName = `${fundName} ${planLabel}`
    const dateFormatted = formatDate(navDate)
    const fundPerformance = performance[scheme.scheme_name]
    const oneYr = fundPerformance?.['1yrret']
      ? parseFloat(fundPerformance['1yrret']).toFixed(2)
      : '—'
    const threeYr = fundPerformance?.['3yearret']
      ? parseFloat(fundPerformance['3yearret']).toFixed(2)
      : '—'
    const fiveYr = fundPerformance?.['5yearret']
      ? parseFloat(fundPerformance['5yearret']).toFixed(2)
      : '—'

    faqData.value[0].question = `What is the current NAV of ${fullFundName}?`
    faqData.value[0].answer = `The latest NAV of ${fullFundName} is ₹${nav} as of ${dateFormatted}.`

    faqData.value[1].question = `What are the historical returns of ${fullFundName}?`
    faqData.value[1].answer = `${fullFundName} has delivered ${oneYr}% over 1 year, ${threeYr}% over 3 years, and ${fiveYr}% over 5 years (as of ${dateFormatted}). Past performance does not guarantee future returns.`

    faqData.value[2].question = `What are the major holdings of ${fullFundName}?`
    faqData.value[2].answer = ` Top holdings may include ${topCompaniesEQ} ${topCompaniesDT}. These holdings are subject to change based on the fund manager’s strategy.`

    faqData.value[3].question = `What is the asset allocation of ${fullFundName}?`
    faqData.value[3].answer = `${fullFundName} Asset allocation for Equity is ${assetAllocationEQ}, Debt is ${assetAllocationDT} & Cash is ${assetAllocationCash}.`

    faqData.value[4].question = `Who is the fund manager of ${fullFundName}?`
    faqData.value[4].answer = `The fund is managed by ${schemeManager}.`

    faqData.value[5].question = `What is the minimum investment amount ${fullFundName}?`
    faqData.value[5].answer = `You can start investing in ${fullFundName} with a minimum of ₹${minimumInvestment}`

    faqData.value[6] = {
      question: `What is the fund’s expense ratio ${fullFundName}?`,
      answer: `The expense ratio for the ${fullFundName} is ${expenseRatio} (as per latest data). This is the annual fee charged by the fund for managing your investment. You can learn more about expense ratio <a href="${baseUrl}/reports/mutual-fund-expense-ratio-all-want-know/" target="_blank" rel="noopener noreferrer">here</a>.`,
    }

    faqData.value[7].question = `How are the gains from ${fullFundName} taxed ?`
    faqData.value[7].answer = `Taxation of mutual funds are generally grouped by categories ${fullFundName} belongs to the ${categoryName} you can find details of taxation of this category <a href="${baseUrl}/reports/mutual-fund-taxation-a-guide/" target="_blank" rel="noopener noreferrer">here</a>`

    faqData.value[8].question = `What type of investor is ${fullFundName} suitable for?`
    faqData.value[8].answer = `${fullFundName} belongs to the ${categoryName}. ${suitabilityText} `
  }
})

const toggleQuestion = (index) => {
  openQuestion.value = openQuestion.value === index ? null : index
}
watchEffect(() => {
  if (scheme.value?.scheme_name && categoryName.value) {
    const cleanName = cleanFundName(scheme.value.scheme_name)
    const plan =
      scheme.value.plan === '5' ? 'Direct - Growth' : 'Regular - Growth'
    const fullFundName = `${cleanName} ${plan}`

    emit('set-fund-info', {
      fundName: fullFundName,
      categoryName: categoryName.value,
    })
  }
})
</script>

<style scoped>
.faq-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
</style>
